/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-6 出力機能 (`/shift/attendance-exports`)。
 * 出力設定 (何をどの列順で出すか) の一覧・作成・編集と、実行 (期間・店舗を指定してCSV/Excelを
 * ダウンロード) を1画面で扱う。バックエンドは実装・レビュー完了済み
 * (apps/api/src/routes/attendance-exports.ts)。
 *
 * ⚠️ 定義の編集はルーティングを増やさず、この画面内で「一覧」⇔「編集」を state で切り替える
 * (ReportViewsSettingsPage.tsx の simple/detail 切替と同型、新規route追加を避ける)。
 *
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §8-5
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, X, Lock, Info, ChevronLeft, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown, Search, Play,
} from 'lucide-react';
import { ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { currentMonthStart } from '@/lib/fiscal-year';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MonthPicker } from '@/components/MonthPicker';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import {
  ATTENDANCE_EXPORT_CATEGORY_ORDER, categoryOfItem, ATTENDANCE_EXPORT_ITEM_TOOLTIPS,
} from '@/lib/attendance-export-categories';
import {
  ATTENDANCE_EXPORT_MAX_COLUMNS,
  type AttendanceExportGrain, type AttendanceExportSensitivity, type AttendanceExportDefinition,
  type AttendanceExportCatalogItem, type AttendanceExportCatalogResponse, type AttendanceExportColumnInput,
  type AttendanceExportAchievementHomeNodeBasis, type AttendanceExportTimeFormat,
  fetchAttendanceExportDefinitions, fetchAttendanceExportCatalog,
  createAttendanceExportDefinition, updateAttendanceExportDefinition, deleteAttendanceExportDefinition,
  replaceAttendanceExportColumns, runAttendanceExport,
} from '@/lib/attendance-export-api';

const GRAIN_LABELS: Record<AttendanceExportGrain, string> = { person_period: '人×期間', person_day: '人×日' };
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
// design-expert CRITICAL是正: type="number" はブラウザ既定のスピナーを付けたままだと誤操作を誘発するため、
// 他ファイル (DenominationMastersPage.tsx 等) と同じ抑制クラスを併用する
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

const errorMessageOf = (e: unknown, fallback: string): string => (e instanceof ApiClientError ? e.message : fallback);

// =============================================================================
// 新規作成モーダル
// =============================================================================
function CreateDefinitionModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (definition: AttendanceExportDefinition) => void;
}) {
  const [label, setLabel] = useState('');
  const [grain, setGrain] = useState<AttendanceExportGrain>('person_period');
  const [fileFormat, setFileFormat] = useState<'xlsx' | 'csv'>('xlsx');

  const createMut = useMutation({
    mutationFn: () => createAttendanceExportDefinition({ label: label.trim(), grain, file_format: fileFormat }),
    onSuccess: (res) => {
      toast.success('作成しました');
      onCreated({
        definition_id: res.definition_id, label: label.trim(), grain, file_format: fileFormat,
        filters_json: null, include_blank_days: true, achievement_month_offset: 0,
        achievement_home_node_basis: 'month_end', achievement_basis_day: null,
        updated_at: new Date().toISOString(), row_version: 1, columns: [],
      });
    },
    onError: (e) => toast.error(errorMessageOf(e, '作成に失敗しました')),
  });

  return (
    <Modal open title="出力設定を新規作成" onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={label.trim().length === 0 || createMut.isPending} loading={createMut.isPending} onClick={() => createMut.mutate()}>作成</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">名称</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} className={inputCls} placeholder="例: 給与計算連携用" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">出力単位</label>
          <Select value={grain} onValueChange={(v) => setGrain(v as AttendanceExportGrain)}>
            <SelectTrigger aria-label="出力単位"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="person_period">人×期間 (1人1行、月の集計)</SelectItem>
              <SelectItem value="person_day">人×日 (1人1日1行、日々の明細)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">ファイル形式</label>
          <Select value={fileFormat} onValueChange={(v) => setFileFormat(v as 'xlsx' | 'csv')}>
            <SelectTrigger aria-label="ファイル形式"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// 実行モーダル (期間・店舗の指定 → 実行 → ダウンロード)
// =============================================================================
const monthIndexOf = (month: string): number => {
  const [y, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return y! * 12 + (m! - 1);
};
const MAX_EXPORT_MONTH_SPAN = 3;

export type ExportRangeValidity = { rangeInvalid: boolean; spanTooLong: boolean; valid: boolean };

/** test-runner HIGH是正: 実行モーダルの期間妥当性判定 (終了<開始・対象スパン超過) を
 *  コンポーネントのクロージャから抜き出した純粋関数。境界値 (ちょうど3か月) はvalid。 */
export const computeExportRangeValidity = (startMonth: string, endMonth: string): ExportRangeValidity => {
  const rangeInvalid = monthIndexOf(endMonth) < monthIndexOf(startMonth);
  const spanTooLong = monthIndexOf(endMonth) - monthIndexOf(startMonth) + 1 > MAX_EXPORT_MONTH_SPAN;
  return { rangeInvalid, spanTooLong, valid: !rangeInvalid && !spanTooLong };
};

/** I-6b (`AttendanceReviewPage.tsx`) の「出力へ進む」からクエリパラメータ (期間・店舗) を
 *  引き継いで遷移してきた場合の事前入力値。無い場合は各項目 undefined。 */
export type CarriedExportFilters = { startMonth?: string; endMonth?: string; nodeIds?: string[] };

function RunExportModal({ definition, initial, onClose }: {
  definition: AttendanceExportDefinition; initial?: CarriedExportFilters; onClose: () => void;
}) {
  const scopeRoots = useScopeStore((s) => s.roots);
  const [startMonth, setStartMonth] = useState(initial?.startMonth ?? currentMonthStart());
  const [endMonth, setEndMonth] = useState(initial?.endMonth ?? currentMonthStart());
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set(initial?.nodeIds ?? []));
  const [showProgress, setShowProgress] = useState(false);

  const { rangeInvalid, spanTooLong } = computeExportRangeValidity(startMonth, endMonth);

  const runMut = useMutation({
    mutationFn: () => runAttendanceExport(
      definition.definition_id,
      { start_month: startMonth, end_month: endMonth, node_ids: selectedNodeIds.size > 0 ? [...selectedNodeIds] : undefined },
      `${definition.label}.${definition.file_format}`,
    ),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('ダウンロードしました');
      onClose();
    },
    onError: (e) => toast.error(errorMessageOf(e, '期間か店舗を絞ってください')),
  });

  // 設計 §8-5: 実行中は進捗表示 (1秒超)
  useEffect(() => {
    if (!runMut.isPending) { setShowProgress(false); return; }
    const timer = setTimeout(() => setShowProgress(true), 1000);
    return () => clearTimeout(timer);
  }, [runMut.isPending]);

  return (
    <Modal open title={`実行: ${definition.label}`} onClose={onClose} size="md" closeOnOverlay={!runMut.isPending}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={runMut.isPending}>キャンセル</Button>
          <Button variant="primary" disabled={rangeInvalid || spanTooLong || definition.columns.length === 0 || runMut.isPending}
            loading={runMut.isPending} loadingLabel={showProgress ? '出力を生成しています…' : undefined} onClick={() => runMut.mutate()}>
            実行してダウンロード
          </Button>
        </>
      )}>
      <div className="space-y-4">
        {definition.columns.length === 0 && (
          <p className="text-xs text-danger-text">この出力設定には列が1つも設定されていません。先に編集画面で列を設定してください。</p>
        )}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-text-sub mb-1">期間開始</label>
            <MonthPicker aria-label="期間開始" value={startMonth} onChange={(v) => setStartMonth(`${v}-01`)}
              className="px-3 py-1.5 rounded border border-surface-border text-sm" />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">期間終了</label>
            <MonthPicker aria-label="期間終了" align="right" value={endMonth} onChange={(v) => setEndMonth(`${v}-01`)}
              className="px-3 py-1.5 rounded border border-surface-border text-sm" />
          </div>
        </div>
        {rangeInvalid && <p className="text-xs text-danger-text">期間終了は期間開始以降にしてください。</p>}
        {!rangeInvalid && spanTooLong && <p className="text-xs text-danger-text">期間か店舗を絞ってください (対象期間は最大3か月です)。</p>}
        <div>
          <label className="block text-xs text-text-sub mb-1">対象店舗 (未選択の場合、閲覧権限のある全店舗が対象)</label>
          <NodeMultiSelect nodes={scopeRoots} selectedIds={selectedNodeIds} onChange={setSelectedNodeIds} disabled={runMut.isPending} />
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// 定義の編集 (項目カタログ + 選択中の列)
// =============================================================================
function ColumnRow({ index, count, column, catalogItem, onMove, onMoveToPosition, onLabelChange, onTimeFormatChange, onRemove }: {
  index: number; count: number; column: AttendanceExportColumnInput; catalogItem: AttendanceExportCatalogItem | undefined;
  onMove: (from: number, delta: -1 | 1 | 'first' | 'last') => void;
  onMoveToPosition: (from: number, pos: number) => void;
  onLabelChange: (index: number, label: string) => void;
  onTimeFormatChange: (index: number, fmt: AttendanceExportTimeFormat) => void;
  onRemove: (index: number) => void;
}) {
  const locked = catalogItem && catalogItem.sensitivity !== 'normal';
  const timeFormat: AttendanceExportTimeFormat = (() => {
    if (!column.format_options_json) return 'hours_decimal';
    try {
      const parsed = JSON.parse(column.format_options_json) as { time_format?: AttendanceExportTimeFormat };
      return parsed.time_format ?? 'hours_decimal';
    } catch {
      return 'hours_decimal';
    }
  })();

  return (
    <li className="border border-surface-border rounded-lg bg-white p-2.5 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-text-sub flex items-center gap-1 min-w-0 flex-1">
          <span className="truncate">{catalogItem?.label ?? column.item_key}</span>
          {locked && <span title="権限が無い人が出力すると空欄になります"><Lock size={12} className="shrink-0 text-text-sub" aria-hidden /></span>}
        </p>
        <button type="button" aria-label="この列を削除" onClick={() => onRemove(index)}
          className="w-11 h-11 sm:w-8 sm:h-8 -mt-1 -mr-1 shrink-0 inline-flex items-center justify-center text-text-sub hover:text-danger-text"><X size={16} /></button>
      </div>
      <input value={column.header_label} onChange={(e) => onLabelChange(index, e.target.value)} maxLength={100}
        aria-label={`列の見出し (${index + 1}列目)`} className={`${inputCls} min-h-[40px] py-1.5`} placeholder="見出し" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-0.5 flex-wrap shrink-0">
          <button type="button" aria-label="先頭へ" disabled={index === 0} onClick={() => onMove(index, 'first')}
            className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronsUp size={14} /></button>
          <button type="button" aria-label="上へ" disabled={index === 0} onClick={() => onMove(index, -1)}
            className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronUp size={14} /></button>
          <button type="button" aria-label="下へ" disabled={index === count - 1} onClick={() => onMove(index, 1)}
            className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronDown size={14} /></button>
          <button type="button" aria-label="末尾へ" disabled={index === count - 1} onClick={() => onMove(index, 'last')}
            className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronsDown size={14} /></button>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <label className="text-[11px] text-text-sub" htmlFor={`col-pos-${index}`}>番号</label>
          <input id={`col-pos-${index}`} type="number" min={1} max={count} defaultValue={index + 1} key={`${index}-${count}`}
            className="w-14 min-h-[44px] sm:min-h-[36px] px-1.5 rounded border border-surface-border text-sm text-center focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const pos = Number.parseInt(e.currentTarget.value, 10);
              if (Number.isFinite(pos)) onMoveToPosition(index, pos);
            }}
            onBlur={(e) => {
              const pos = Number.parseInt(e.currentTarget.value, 10);
              if (Number.isFinite(pos)) onMoveToPosition(index, pos);
            }} />
        </div>
      </div>
      {catalogItem?.value_kind === 'minutes' && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-text-sub shrink-0" htmlFor={`col-fmt-${index}`}>表示形式</label>
          <Select value={timeFormat} onValueChange={(v) => onTimeFormatChange(index, v as AttendanceExportTimeFormat)}>
            <SelectTrigger id={`col-fmt-${index}`} className="text-xs w-40" aria-label={`表示形式 (${index + 1}列目)`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hours_decimal">時間 (小数、例: 8.50)</SelectItem>
              <SelectItem value="hours_minutes">時:分 (例: 8:30)</SelectItem>
              <SelectItem value="minutes">分のみ (例: 510)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </li>
  );
}

/** test-runner HIGH是正: 列の並べ替えロジックをコンポーネントのクロージャから抜き出した純粋関数。
 *  to<0/to>=length は先頭/末尾へクランプし、同一位置なら (参照同一の) 元の配列をそのまま返す。 */
export const moveColumnTo = <T,>(columns: readonly T[], from: number, to: number): T[] => {
  const clamped = Math.max(0, Math.min(columns.length - 1, to));
  if (clamped === from) return [...columns];
  const next = [...columns];
  const [row] = next.splice(from, 1);
  next.splice(clamped, 0, row!);
  return next;
};

/** test-runner HIGH是正: 出力単位を切り替える際に「選択中の列が全て削除される」確認ダイアログを
 *  出すべきかどうかの判定。同一出力単位への変更・列が1つも無い場合は確認不要。 */
export const shouldConfirmGrainSwitch = (
  next: AttendanceExportGrain, current: AttendanceExportGrain, columnCount: number,
): boolean => {
  if (next === current) return false;
  return columnCount > 0;
};

function DefinitionEditor({ definition, catalog, onBack, onChanged }: {
  definition: AttendanceExportDefinition; catalog: AttendanceExportCatalogResponse; onBack: () => void; onChanged: () => void;
}) {
  const [label, setLabel] = useState(definition.label);
  const [grain, setGrain] = useState<AttendanceExportGrain>(definition.grain);
  const [fileFormat, setFileFormat] = useState(definition.file_format);
  const [includeBlankDays, setIncludeBlankDays] = useState(definition.include_blank_days);
  const [achievementMonthOffset, setAchievementMonthOffset] = useState(definition.achievement_month_offset);
  const [achievementHomeNodeBasis, setAchievementHomeNodeBasis] = useState<AttendanceExportAchievementHomeNodeBasis>(definition.achievement_home_node_basis);
  const [achievementBasisDay, setAchievementBasisDay] = useState<number | null>(definition.achievement_basis_day);
  const [columns, setColumns] = useState<AttendanceExportColumnInput[]>(
    definition.columns.map((c) => ({ item_key: c.item_key, header_label: c.header_label, format_options_json: c.format_options_json })),
  );
  const [rowVersion, setRowVersion] = useState(definition.row_version);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [grainSwitchTarget, setGrainSwitchTarget] = useState<AttendanceExportGrain | null>(null);
  // QA Major是正: 「一覧へ戻る」が dirty を無視して即座に破棄していたため、未保存変更がある時だけ確認する
  const [backConfirmOpen, setBackConfirmOpen] = useState(false);

  const catalogByKey = useMemo(() => {
    const map = new Map<string, AttendanceExportCatalogItem>();
    for (const item of [...catalog.person_period, ...catalog.person_day]) map.set(item.item_key, item);
    return map;
  }, [catalog]);
  const catalogItems = catalog[grain];
  const selectedKeys = useMemo(() => new Set(columns.map((c) => c.item_key)), [columns]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? catalogItems.filter((item) => item.label.toLowerCase().includes(q)) : catalogItems;
  }, [catalogItems, search]);

  const categorized = useMemo(() => {
    const map = new Map<string, AttendanceExportCatalogItem[]>();
    for (const item of filteredCatalog) {
      const cat = categoryOfItem(item.item_key);
      const list = map.get(cat) ?? [];
      list.push(item);
      map.set(cat, list);
    }
    return ATTENDANCE_EXPORT_CATEGORY_ORDER
      .map((cat) => ({ category: cat, items: map.get(cat) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [filteredCatalog]);

  const includesSensitivity = (s: AttendanceExportSensitivity) =>
    columns.some((c) => catalogByKey.get(c.item_key)?.sensitivity === s);

  const toggleItem = (item: AttendanceExportCatalogItem) => {
    setDirty(true);
    if (selectedKeys.has(item.item_key)) {
      setColumns((cols) => cols.filter((c) => c.item_key !== item.item_key));
      return;
    }
    if (columns.length >= ATTENDANCE_EXPORT_MAX_COLUMNS) {
      toast.error(`列は最大${ATTENDANCE_EXPORT_MAX_COLUMNS}件までです`);
      return;
    }
    setColumns((cols) => [...cols, { item_key: item.item_key, header_label: item.label, format_options_json: null }]);
  };

  const moveTo = (from: number, to: number) => {
    setColumns((cols) => moveColumnTo(cols, from, to));
    setDirty(true);
  };
  const onMove = (index: number, delta: -1 | 1 | 'first' | 'last') => {
    if (delta === 'first') { moveTo(index, 0); return; }
    if (delta === 'last') { moveTo(index, columns.length - 1); return; }
    moveTo(index, index + delta);
  };
  const onMoveToPosition = (index: number, pos: number) => moveTo(index, pos - 1);
  const updateHeaderLabel = (index: number, value: string) => {
    setColumns((cols) => cols.map((c, i) => (i === index ? { ...c, header_label: value } : c)));
    setDirty(true);
  };
  const updateTimeFormat = (index: number, fmt: AttendanceExportTimeFormat) => {
    setColumns((cols) => cols.map((c, i) => (i === index ? { ...c, format_options_json: JSON.stringify({ time_format: fmt }) } : c)));
    setDirty(true);
  };
  const removeColumn = (index: number) => {
    setColumns((cols) => cols.filter((_, i) => i !== index));
    setDirty(true);
  };

  const requestGrainChange = (next: AttendanceExportGrain) => {
    if (next === grain) return;
    if (shouldConfirmGrainSwitch(next, grain, columns.length)) { setGrainSwitchTarget(next); return; }
    setGrain(next);
    setDirty(true);
  };
  const confirmGrainChange = () => {
    if (!grainSwitchTarget) return;
    setGrain(grainSwitchTarget);
    setColumns([]);
    setDirty(true);
    setGrainSwitchTarget(null);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      await updateAttendanceExportDefinition(definition.definition_id, {
        label: label.trim(), grain, file_format: fileFormat, include_blank_days: includeBlankDays,
        achievement_month_offset: achievementMonthOffset, achievement_home_node_basis: achievementHomeNodeBasis,
        achievement_basis_day: achievementBasisDay, row_version: rowVersion,
      });
      await replaceAttendanceExportColumns(definition.definition_id, columns);
    },
    onSuccess: () => {
      setRowVersion((v) => v + 1);
      setDirty(false);
      onChanged();
      toast.success('保存しました');
    },
    onError: (e) => {
      toast.error(errorMessageOf(e, '保存に失敗しました'));
      // QA Major是正: 409 (ROW_VERSION_MISMATCH) は rowVersion/columns が古いまま残り再送しても
      // 必ず失敗し続けるため、一覧へ戻して最新データを再取得させる
      // (WorkRecordApprovalsPage.tsx の ROW_VERSION_MISMATCH 対処と同型)
      if (e instanceof ApiClientError && e.code === 'ROW_VERSION_MISMATCH') { onChanged(); onBack(); }
    },
  });

  return (
    <div className="space-y-4">
      <button type="button" onClick={() => (dirty ? setBackConfirmOpen(true) : onBack())}
        className="inline-flex items-center gap-1 min-h-[44px] text-sm text-text-sub hover:text-text-body">
        <ChevronLeft size={16} aria-hidden /> 一覧へ戻る
      </button>

      <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <label className="block text-xs text-text-sub mb-1">名称</label>
            <input value={label} onChange={(e) => { setLabel(e.target.value); setDirty(true); }} maxLength={100} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">出力単位</label>
            <Select value={grain} onValueChange={(v) => requestGrainChange(v as AttendanceExportGrain)}>
              <SelectTrigger aria-label="出力単位"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="person_period">人×期間</SelectItem>
                <SelectItem value="person_day">人×日</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">ファイル形式</label>
            <Select value={fileFormat} onValueChange={(v) => { setFileFormat(v as 'xlsx' | 'csv'); setDirty(true); }}>
              <SelectTrigger aria-label="ファイル形式"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {grain === 'person_day' && (
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5 accent-brand-red" checked={includeBlankDays}
              onChange={(e) => { setIncludeBlankDays(e.target.checked); setDirty(true); }} />
            記録の無い日も空欄行として出力する
          </label>
        )}
        {includesSensitivity('sales') && (
          <div className="border-t border-surface-border pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <p className="sm:col-span-3 text-xs text-text-sub">目標達成率の対象月は、以下の基準日が属する月から求めます。</p>
            <div>
              <label className="block text-xs text-text-sub mb-1">対象月のオフセット</label>
              <input type="number" min={-12} max={12} value={achievementMonthOffset}
                onChange={(e) => { setAchievementMonthOffset(Number(e.target.value)); setDirty(true); }} className={numberInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">主所属店舗の基準日</label>
              <Select value={achievementHomeNodeBasis} onValueChange={(v) => { setAchievementHomeNodeBasis(v as AttendanceExportAchievementHomeNodeBasis); setDirty(true); }}>
                <SelectTrigger aria-label="主所属店舗の基準日"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month_start">対象月の月初</SelectItem>
                  <SelectItem value="month_end">対象月の月末</SelectItem>
                  <SelectItem value="fixed_day">対象月の指定日</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {achievementHomeNodeBasis === 'fixed_day' && (
              <div>
                <label className="block text-xs text-text-sub mb-1">指定日 (1〜31)</label>
                <input type="number" min={1} max={31} value={achievementBasisDay ?? ''}
                  onChange={(e) => { setAchievementBasisDay(e.target.value ? Number(e.target.value) : null); setDirty(true); }} className={numberInputCls} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/2 min-w-0 border border-surface-border rounded-lg bg-white p-3 space-y-2">
          <p className="text-sm font-semibold text-text-body">項目カタログ ({columns.length}/{ATTENDANCE_EXPORT_MAX_COLUMNS}列選択中)</p>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-sub" aria-hidden />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="項目を検索"
              aria-label="項目を検索" className={`${inputCls} pl-8`} />
          </div>
          <div className="max-h-[28rem] overflow-y-auto space-y-3 pr-1">
            {categorized.length === 0 ? (
              <p className="text-sm text-text-sub py-4 text-center">該当する項目がありません</p>
            ) : categorized.map((group) => (
              <div key={group.category}>
                <p className="text-xs font-semibold text-text-sub mb-1">{group.category}</p>
                {(group.category === '家族' || group.category === '目標達成率') && (
                  <p className="text-[11px] text-text-sub mb-1 flex items-center gap-1">
                    <Lock size={11} className="shrink-0" aria-hidden />権限が無い人が出力すると空欄になります
                  </p>
                )}
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const checked = selectedKeys.has(item.item_key);
                    // design-and-code Medium是正: runnable=false の項目は選択しても実行時に必ずエラーになるため、
                    // 選択済みで無い限り選択不可にする (§13-33 in_probation 等)
                    const disabledAdd = !checked && (!item.runnable || columns.length >= ATTENDANCE_EXPORT_MAX_COLUMNS);
                    const tooltip = ATTENDANCE_EXPORT_ITEM_TOOLTIPS[item.item_key];
                    return (
                      <li key={item.item_key}>
                        <label className={`flex items-center gap-2 min-h-[44px] px-2 rounded hover:bg-surface-sunken ${disabledAdd ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" className="w-4 h-4 accent-brand-red shrink-0" checked={checked} disabled={disabledAdd}
                            onChange={() => toggleItem(item)} />
                          <span className="text-sm text-text-body truncate flex-1">{item.label}</span>
                          {!item.runnable && (
                            <span className="text-[10px] text-text-sub shrink-0" title="現在この項目は出力に対応していません">(未対応)</span>
                          )}
                          {item.sensitivity !== 'normal' && (
                            <span title="権限が無い人が出力すると空欄になります"><Lock size={13} className="text-text-sub shrink-0" aria-hidden /></span>
                          )}
                          {tooltip && <span title={tooltip}><Info size={13} className="text-text-sub shrink-0" aria-hidden /></span>}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="md:w-1/2 min-w-0 border border-surface-border rounded-lg bg-white p-3 space-y-2">
          <p className="text-sm font-semibold text-text-body">選択中の列 ({columns.length}件)</p>
          {columns.length === 0 ? (
            <EmptyState message="列がまだ選択されていません" description="左の項目カタログから追加してください。" />
          ) : (
            <ul className="max-h-[28rem] overflow-y-auto space-y-1.5 pr-1">
              {columns.map((c, i) => (
                <ColumnRow key={c.item_key} index={i} count={columns.length} column={c} catalogItem={catalogByKey.get(c.item_key)}
                  onMove={onMove} onMoveToPosition={onMoveToPosition} onLabelChange={updateHeaderLabel}
                  onTimeFormatChange={updateTimeFormat} onRemove={removeColumn} />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
        <p className="text-sm font-semibold text-text-body">見出しのプレビュー</p>
        {columns.length === 0 ? (
          <p className="text-xs text-text-sub">列を選択すると、ここに見出しの並びが表示されます。</p>
        ) : (
          <div className="flex flex-col md:flex-row md:overflow-x-auto gap-1.5 md:gap-0">
            {columns.map((c, i) => (
              <div key={c.item_key}
                className="shrink-0 px-2.5 py-1.5 text-xs text-text-body bg-surface-sunken border border-surface-border md:border-r-0 md:last:border-r first:rounded-t md:first:rounded-t-none md:first:rounded-l last:rounded-b md:last:rounded-b-none md:last:rounded-r whitespace-nowrap">
                {i + 1}. {c.header_label || '(見出し未設定)'}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button variant="primary" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存する</Button>
      </div>

      <ConfirmDialog open={!!grainSwitchTarget} title="出力単位を変更しますか"
        message={`出力単位を「${grainSwitchTarget ? GRAIN_LABELS[grainSwitchTarget] : ''}」に変更すると、選択中の列 (${columns.length}件) が全て削除されます。`}
        onConfirm={confirmGrainChange} onClose={() => setGrainSwitchTarget(null)} />
      <ConfirmDialog open={backConfirmOpen} title="一覧へ戻りますか" confirmLabel="戻る"
        message="保存していない変更は破棄されます。一覧へ戻ってよろしいですか。"
        onConfirm={onBack} onClose={() => setBackConfirmOpen(false)} />
    </div>
  );
}

// =============================================================================
// 一覧
// =============================================================================
export function AttendanceExportsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'attendance_export', 'manage');

  // I-6b (勤怠実績の確認画面) の「出力へ進む」から期間・店舗を引き継いで遷移してきた場合、
  // 実行モーダルの初期値として使う (§13-39 QA/design-and-code是正: 従来は消費されずdead-endだった)
  const [searchParams] = useSearchParams();
  const carriedFilters: CarriedExportFilters | null = useMemo(() => {
    const startMonth = searchParams.get('start_month');
    const endMonth = searchParams.get('end_month');
    if (!startMonth || !endMonth) return null;
    const nodeIdsParam = searchParams.get('node_ids');
    return { startMonth, endMonth, nodeIds: nodeIdsParam ? nodeIdsParam.split(',').filter(Boolean) : undefined };
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['attendance-exports'],
    queryFn: fetchAttendanceExportDefinitions,
  });
  const { data: catalogData } = useQuery({
    queryKey: ['attendance-exports-catalog'],
    queryFn: fetchAttendanceExportCatalog,
  });

  const [definitionsOverride, setDefinitionsOverride] = useState<AttendanceExportDefinition[] | null>(null);
  const definitions = definitionsOverride ?? data?.definitions ?? [];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [runTarget, setRunTarget] = useState<AttendanceExportDefinition | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AttendanceExportDefinition | null>(null);

  const onChanged = () => {
    setDefinitionsOverride(null);
    qc.invalidateQueries({ queryKey: ['attendance-exports'] });
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAttendanceExportDefinition(id),
    onSuccess: () => { onChanged(); toast.success('削除しました'); setDeleteTarget(null); },
    onError: (e) => { toast.error(errorMessageOf(e, '削除に失敗しました')); setDeleteTarget(null); },
  });

  const editing = definitions.find((d) => d.definition_id === editingId) ?? null;

  if (editing && catalogData) {
    return (
      <DefinitionEditor key={editing.definition_id} definition={editing} catalog={catalogData}
        onBack={() => setEditingId(null)} onChanged={onChanged} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-h2">勤怠実績の出力設定</h2>
          <p className="text-sm text-text-sub mt-1">給与計算等に渡すためのCSV/Excel出力の項目・列順を設定し、期間・店舗を指定して実行します。</p>
        </div>
        {canManage && <Button variant="primary" leftIcon={<Plus size={16} aria-hidden />} onClick={() => setCreateOpen(true)}>新規作成</Button>}
      </div>

      {carriedFilters && (
        <div className="flex items-start gap-2 text-sm text-text-body bg-surface-sunken border border-surface-border rounded px-3 py-2">
          <Info size={16} className="mt-0.5 shrink-0 text-text-sub" aria-hidden />
          <div className="min-w-0">勤怠実績の確認画面で指定していた期間・店舗を引き継いでいます。「実行」を押すと、そのまま初期値として反映されます。</div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text">{errorMessageOf(error, '取得に失敗しました')}</p>
      ) : definitions.length === 0 ? (
        <EmptyState message="出力設定がまだありません"
          description={canManage ? '「新規作成」から作成してください。' : '管理者が出力設定を作成すると、ここから実行できるようになります。'} />
      ) : (
        <ul className="space-y-2">
          {definitions.map((d) => (
            <li key={d.definition_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body break-words">{d.label}</p>
                <p className="text-xs text-text-sub mt-0.5">
                  {GRAIN_LABELS[d.grain]} / {d.file_format.toUpperCase()} / {d.columns.length}列
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="secondary" size="sm" leftIcon={<Play size={14} aria-hidden />} onClick={() => setRunTarget(d)}>実行</Button>
                {canManage && <Button variant="ghost" size="sm" onClick={() => setEditingId(d.definition_id)}>編集</Button>}
                {canManage && <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(d)}>削除</Button>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateDefinitionModal onClose={() => setCreateOpen(false)}
          onCreated={(def) => {
            setDefinitionsOverride([...(data?.definitions ?? []), def]);
            setCreateOpen(false);
            setEditingId(def.definition_id);
          }} />
      )}
      {runTarget && <RunExportModal definition={runTarget} initial={carriedFilters ?? undefined} onClose={() => setRunTarget(null)} />}
      <ConfirmDialog open={!!deleteTarget} title="出力設定を削除しますか" destructive
        message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.definition_id)}
        onClose={() => setDeleteTarget(null)} />
    </div>
  );
}
